import { useEffect, useState } from "react";
import NotFoundPage from "../../NotFoundPage";
import Loader from "../../../../components/Loader";
import { useInjectReducer, useInjectSaga } from "redux-injectors";
import { useDispatch, useSelector } from "react-redux";
import { actions, reducer, sliceKey } from "./slice";
import { assetDetailsSaga } from "./saga";
import * as selectors from "./selectors";
import {
  PageHeader,
  Row,
  Col,
  Typography,
  Tag,
  Table,
  Tabs,
  Image,
  Tooltip,
  Dropdown,
  Menu,
  Button,
  message,
} from "antd";
import { EditOutlined, MoreOutlined } from "@ant-design/icons";
import {
  useHistory,
  useParams,
} from "react-router-dom/cjs/react-router-dom.min";
import { dateToString } from "../../../../utils/formatter";
import EditProjectCode from "./components/EditProjectCode";
import AddSeals from "./components/AddSeals";
import AddBoxes from "./components/AddBoxes";
import ModalAfterAssetCreation from "./components/ModalAfterAssetCreation";
import DeleteKit from "./components/DeleteKit";
import ManageBoxOrders from "./components/ManageBoxOrders";
import ChangeBoxStatus from "./components/ChangeBoxStatus";
import { useAuth } from "../../../../contexts/auth";
import AddStickers from "./components/AddStickers";
import AddDevices from "./components/AddDevices";

const { TabPane } = Tabs;
const { Text } = Typography;
const assetStatusColors = {
  Unused: "blue",
  Used: "green",
  Damaged: "red",
  Unpacked: "orange",
  Packed: "blue",
};

const statusColors = {
  Packed: "blue",
  Pending: "gold",
  "Out for delivery": "purple",
  Delivered: "green",
};

const getSealTypeColor = (label) => {
  if (label?.toLowerCase().includes("lab")) return "blue";
  if (label?.toLowerCase().includes("reference")) return "pink";
  return "default";
};

const AssetTable = ({ dataSource, columns, rowKey }) => (
  <Table
    dataSource={dataSource}
    columns={columns}
    pagination={false}
    bordered={false}
    style={{
      background: "white",
      marginBottom: 24,
    }}
    rowKey={rowKey}
    scroll={{ x: 1000, y: "calc(100vh - 433px)" }}
  />
);

export default function AssetDetails() {
  useInjectReducer({ key: sliceKey, reducer: reducer });
  useInjectSaga({ key: sliceKey, saga: assetDetailsSaga });
  const dispatch = useDispatch();
  const history = useHistory();
  const auth = useAuth();
  const { loggedInUser } = auth;
  const userRole = loggedInUser.role;
  const { assetId } = useParams();

  const loading = useSelector(selectors.selectLoading);
  const KitDetail = useSelector(selectors.selectKitDetails);
  const error = useSelector(selectors.selectError);
  const [isEditProjectModalOpen, setIsEditProjectModalOpen] = useState(false);
  const [loadingSealId, setLoadingSealId] = useState(null);

  const [kitDataAfterAssetCreation, setKitDataAfterAssetCreation] = useState(
    {}
  );
  const [isOpenModalAfterAssetCreation, setIsOpenModalAfterAssetCreation] =
    useState(false);
  const [selectedBox, setSelectedBox] = useState(null);
  const [isManageOrdersModalOpen, setIsManageOrdersModalOpen] = useState(false);
  const [isChangeStatusModalOpen, setIsChangeStatusModalOpen] = useState(false);
  const [targetStatus, setTargetStatus] = useState(null);

  const handleMarkAsUnused = (sealId) => {
    setLoadingSealId(sealId);
    dispatch(
      actions.markSealAsUnused({
        sampleSealId: sealId,
        onSuccess: (data) => {
          message.success("Seal marked as unused successfully");
          setLoadingSealId(null);
          fetchKitDetail();
        },
        onFailure: (error) => {
          message.error(error?.message || "Failed to mark seal as unused");
          setLoadingSealId(null);
        },
      })
    );
  };

  const sealColumns = [
    {
      title: (
        <span style={{ color: "#8A8FA3", fontWeight: 700, fontSize: 15 }}>
          Seal ID
        </span>
      ),
      dataIndex: "sealId",
      key: "sealId",
      render: (text) => (
        <span style={{ fontWeight: 600, fontSize: 15 }}>{text}</span>
      ),
    },
    {
      title: (
        <span style={{ color: "#8A8FA3", fontWeight: 700, fontSize: 15 }}>
          Seal Type
        </span>
      ),
      dataIndex: "sealType",
      key: "sealType",
      filters: [
        { text: "Lab", value: "Lab" },
        { text: "Reference", value: "Reference" },
      ],
      onFilter: (value, record) => record.sealType === value,
      render: (text) => (
        <Tag
          color={getSealTypeColor(text)}
          style={{
            borderRadius: 8,
            fontWeight: 700,
            fontSize: 14,
            padding: "2px 12px",
            border: "none",
          }}
        >
          {text}
        </Tag>
      ),
    },
    {
      title: (
        <span style={{ color: "#8A8FA3", fontWeight: 700, fontSize: 15 }}>
          Status
        </span>
      ),
      dataIndex: "status",
      key: "status",
      render: (status) => (
        <Tag
          color={assetStatusColors[status]}
          style={{
            borderRadius: 8,
            fontWeight: 700,
            fontSize: 15,
            padding: "2px 12px",
            border: "none",
          }}
        >
          {status}
        </Tag>
      ),
    },
    {
      title: (
        <span style={{ color: "#8A8FA3", fontWeight: 700, fontSize: 15 }}>
          Order ID
        </span>
      ),
      dataIndex: "orderId",
      key: "orderId",
      render: (text) => (
        <span style={{ fontWeight: 500, fontSize: 15 }}>{text}</span>
      ),
    },
    {
      title: (
        <span style={{ color: "#8A8FA3", fontWeight: 700, fontSize: 15 }}>
          Actions
        </span>
      ),
      key: "actions",
      render: (_, record) => {
        const isUnused = record.status?.toLowerCase() === "unused";
        const isLoading = loadingSealId === record.key;

        return (
          <Button
            type="primary"
            size="small"
            disabled={isUnused}
            loading={isLoading}
            onClick={() => handleMarkAsUnused(record.key)}
            style={{
              borderRadius: 6,
              fontWeight: 600,
            }}
          >
            Mark as Unused
          </Button>
        );
      },
    },
  ];

  const boxColumns = [
    {
      title: (
        <span style={{ color: "#8A8FA3", fontWeight: 700, fontSize: 15 }}>
          Box ID
        </span>
      ),
      dataIndex: "boxId",
      key: "boxId",
      render: (text) => (
        <span style={{ fontWeight: 600, fontSize: 15 }}>{text}</span>
      ),
    },
    {
      title: (
        <span style={{ color: "#8A8FA3", fontWeight: 700, fontSize: 15 }}>
          Status
        </span>
      ),
      dataIndex: "status",
      key: "status",
      render: (status) => (
        <Tag
          color={assetStatusColors[status]}
          style={{
            borderRadius: 8,
            fontWeight: 700,
            fontSize: 15,
            padding: "2px 12px",
            border: "none",
          }}
        >
          {status}
        </Tag>
      ),
    },
    {
      title: (
        <span style={{ color: "#8A8FA3", fontWeight: 700, fontSize: 15 }}>
          Tracking Code
        </span>
      ),
      dataIndex: "trackingCode",
      key: "trackingCode",
    },
    {
      title: (
        <span style={{ color: "#8A8FA3", fontWeight: 700, fontSize: 15 }}>
          Associated Order IDs
        </span>
      ),
      dataIndex: "orderIds",
      key: "orderIds",
      // width: 180,
      render: (orders) => (
        <div>
          {Array.isArray(orders) && orders.length > 0
            ? orders.map((order) => (
                <div key={order?._id}>{order?.orderId}</div>
              ))
            : "-"}
        </div>
      ),
    },
    {
      title: (
        <span style={{ color: "#8A8FA3", fontWeight: 700, fontSize: 15 }}>
          Image
        </span>
      ),
      dataIndex: "image",
      key: "image",
      render: (images) => (
        <div style={{ display: "flex", flexDirection: "row" }}>
          {Array.isArray(images) && images.length > 0
            ? images.map((img, idx) => (
                <span key={idx} style={{ marginRight: 8 }}>
                  <Image
                    src={img}
                    alt={`img-${idx}`}
                    width={40}
                    height={40}
                    style={{
                      cursor: "pointer",
                      objectFit: "contain",
                    }}
                    preview={{
                      mask: "view",
                    }}
                  />
                </span>
              ))
            : "-"}
        </div>
      ),
    },
    {
      title: (
        <span style={{ color: "#8A8FA3", fontWeight: 700, fontSize: 15 }}>
          Actions
        </span>
      ),
      key: "actions",
      render: (_, record) => {
        const currentStatus =
          record.statusValue?.toLowerCase() || record.status?.toLowerCase();

        const handleMenuClick = ({ key }) => {
          if (key === "manage-orders") {
            setSelectedBox(record);
            setIsManageOrdersModalOpen(true);
          } else if (key === "move-to-packed") {
            setSelectedBox(record);
            setTargetStatus("packed");
            setIsChangeStatusModalOpen(true);
          } else if (key === "move-to-dispatched") {
            setSelectedBox(record);
            setTargetStatus("dispatched");
            setIsChangeStatusModalOpen(true);
          }
        };

        const menu = (
          <Menu onClick={handleMenuClick}>
            <Menu.Item key="manage-orders">Manage Box Orders</Menu.Item>

            {currentStatus !== "packed" && currentStatus !== "dispatched" && (
              <Menu.Item key="move-to-packed">Move to Packed</Menu.Item>
            )}

            {currentStatus === "packed" && (
              <Menu.Item key="move-to-dispatched">Move to Dispatch</Menu.Item>
            )}
          </Menu>
        );

        return (
          <Dropdown overlay={menu} trigger={["click"]} placement="bottomRight">
            <Button
              type="text"
              icon={
                <MoreOutlined style={{ fontSize: 20, fontWeight: "bold" }} />
              }
              style={{ padding: 0 }}
            />
          </Dropdown>
        );
      },
    },
  ];

  const stickerColumns = [
    {
      title: (
        <span style={{ color: "#8A8FA3", fontWeight: 700, fontSize: 15 }}>
          Sticker ID
        </span>
      ),
      dataIndex: "stickerId",
      key: "stickerId",
      render: (text) => (
        <span style={{ fontWeight: 600, fontSize: 15 }}>{text}</span>
      ),
    },
    {
      title: (
        <span style={{ color: "#8A8FA3", fontWeight: 700, fontSize: 15 }}>
          Status
        </span>
      ),
      dataIndex: "status",
      key: "status",
      render: (status) => (
        <Tag
          color={assetStatusColors[status] || "default"}
          style={{
            borderRadius: 8,
            fontWeight: 700,
            fontSize: 15,
            padding: "2px 12px",
            border: "none",
          }}
        >
          {status || "-"}
        </Tag>
      ),
    },
    {
      title: (
        <span style={{ color: "#8A8FA3", fontWeight: 700, fontSize: 15 }}>
          Order ID
        </span>
      ),
      dataIndex: "orderId",
      key: "orderId",
      render: (text) => (
        <span style={{ fontWeight: 500, fontSize: 15 }}>{text}</span>
      ),
    },
  ];

  const deviceColumns = [
    {
      title: (
        <span style={{ color: "#8A8FA3", fontWeight: 700, fontSize: 15 }}>
          Device Name
        </span>
      ),
      dataIndex: "deviceName",
      key: "deviceName",
      render: (text) => (
        <span style={{ fontWeight: 600, fontSize: 15 }}>{text}</span>
      ),
    },
    {
      title: (
        <span style={{ color: "#8A8FA3", fontWeight: 700, fontSize: 15 }}>
          Unique ID
        </span>
      ),
      dataIndex: "uniqueId",
      key: "uniqueId",
      render: (text) => (
        <span style={{ fontWeight: 600, fontSize: 15 }}>{text}</span>
      ),
    },
    {
      title: (
        <span style={{ color: "#8A8FA3", fontWeight: 700, fontSize: 15 }}>
          Model
        </span>
      ),
      dataIndex: "deviceModel",
      key: "deviceModel",
      render: (text) => (
        <span style={{ fontWeight: 500, fontSize: 15 }}>{text}</span>
      ),
    },
    {
      title: (
        <span style={{ color: "#8A8FA3", fontWeight: 700, fontSize: 15 }}>
          Description
        </span>
      ),
      dataIndex: "description",
      key: "description",
      render: (text) => (
        <span style={{ fontWeight: 500, fontSize: 15 }}>{text}</span>
      ),
    },
  ];

  const sealData = KitDetail?.sampleSeals?.map((seal) => ({
    key: seal?._id,
    sealId: seal?.sampleSealNumber,
    sealType: seal?.sealType?.label?.split(" ")[0] || "-",
    status: seal?.status?.label,
    orderId: seal?.orderId || "-",
  }));

  const boxData = KitDetail?.samplerBoxes?.map((box) => ({
    key: box?._id,
    _id: box?._id,
    boxId: box?.boxId,
    trackingCode: box?.trackingCode || "-",
    status: box?.status?.label,
    statusValue: box?.status?.value,
    orderIds: box?.samples || [],
    image: [...box.packedImages, ...box.dispatchImages],
    fullBoxData: box,
  }));

  const stickerData = KitDetail?.sampleStickers?.map((sticker) => ({
    key: sticker?._id,
    stickerId: sticker?.sampleStickerNumber,
    status: sticker?.status?.label,
    orderId: sticker?.orderId || "-",
  }));

  const deviceData = KitDetail?.devices?.map((device) => ({
    key: device?._id,
    deviceName: device?.deviceName,
    uniqueId: device?.uniqueId,
    deviceModel: device?.deviceModel || "-",
    description: device?.description || "-",
  }));

  const fetchKitDetail = (isLoading = true) => {
    dispatch(
      actions.fetchKitDetails({
        assetId,
        isLoading: isLoading,
        onSuccess: () => {},
        onFailure: (error) => {},
      })
    );
  };

  useEffect(() => {
    fetchKitDetail();
    dispatch(actions.fetchProjects());
  }, [dispatch, assetId]);

  const status = KitDetail?.status?.value;
  const disabledActions = !["pending", "packed"].includes(status);

  return loading ? (
    <Loader />
  ) : error ? (
    <NotFoundPage />
  ) : (
    <div>
      <PageHeader
        ghost={false}
        title={`Kit Details: ${KitDetail?.kitNumber || ""}`}
        onBack={() => history.goBack()}
        extra={
          userRole !== "agnext_reviewer"
            ? [
                <AddSeals
                  key="add-seals"
                  KitDetail={KitDetail}
                  fetchKitDetail={fetchKitDetail}
                  setKitDataAfterAssetCreation={setKitDataAfterAssetCreation}
                  setIsOpenModalAfterAssetCreation={
                    setIsOpenModalAfterAssetCreation
                  }
                  disabledActions={disabledActions}
                />,
                <AddBoxes
                  key="add-boxes"
                  KitDetail={KitDetail}
                  fetchKitDetail={fetchKitDetail}
                  setKitDataAfterAssetCreation={setKitDataAfterAssetCreation}
                  setIsOpenModalAfterAssetCreation={
                    setIsOpenModalAfterAssetCreation
                  }
                  disabledActions={disabledActions}
                />,
                <AddStickers
                  key="add-stickers"
                  KitDetail={KitDetail}
                  fetchKitDetail={fetchKitDetail}
                  setKitDataAfterAssetCreation={setKitDataAfterAssetCreation}
                  setIsOpenModalAfterAssetCreation={
                    setIsOpenModalAfterAssetCreation
                  }
                  disabledActions={disabledActions}
                />,
                <AddDevices
                  key="add-devices"
                  KitDetail={KitDetail}
                  fetchKitDetail={fetchKitDetail}
                  setKitDataAfterAssetCreation={setKitDataAfterAssetCreation}
                  setIsOpenModalAfterAssetCreation={
                    setIsOpenModalAfterAssetCreation
                  }
                  disabledActions={disabledActions}
                />,
                <DeleteKit
                  key="delete-kit"
                  KitDetail={KitDetail}
                  disabledActions={disabledActions}
                />,
              ]
            : []
        }
      />
      <div
        style={{
          padding: "24px 24px 0px 24px",
          display: "flex",
          flexDirection: "column",
          gap: 16,
          overflow: "auto",
        }}
      >
        <Row
          gutter={0}
          style={{ background: "#fff", padding: 24, borderRadius: 16 }}
        >
          <Col span={6}>
            <Text style={{ color: "#8A8FA3", fontWeight: 600, fontSize: 16 }}>
              Project Code
            </Text>
            <div
              style={{
                fontWeight: 600,
                fontSize: 16,
                color: "#232A36",
                marginTop: 2,
              }}
            >
              {KitDetail?.projectCode?.projectcode || "-"}
              {userRole !== "agnext_reviewer" && (
                <Tooltip
                  title={
                    disabledActions
                      ? "Edit is only allowed when the kit is in the Pending or Packed stage"
                      : ""
                  }
                >
                  <EditOutlined
                    style={{
                      color: disabledActions ? "#d9d9d9" : "#8A8FA3",
                      cursor: disabledActions ? "not-allowed" : "pointer",
                      margin: 10,
                    }}
                    onClick={() => {
                      if (disabledActions) return;
                      setIsEditProjectModalOpen(true);
                    }}
                  />
                </Tooltip>
              )}
            </div>
          </Col>

          <Col span={6}>
            <Text style={{ color: "#8A8FA3", fontWeight: 600, fontSize: 16 }}>
              Sampler
            </Text>
            <div
              style={{
                fontWeight: 600,
                fontSize: 16,
                color: "#232A36",
                marginTop: 2,
              }}
            >
              {KitDetail?.sampler?.name || "-"}
            </div>
          </Col>
          <Col span={6}>
            <Text style={{ color: "#8A8FA3", fontWeight: 600, fontSize: 16 }}>
              Assigned Date
            </Text>
            <div
              style={{
                fontWeight: 600,
                fontSize: 16,
                color: "#232A36",
                marginTop: 2,
              }}
            >
              {KitDetail?.assignedDate
                ? dateToString(KitDetail?.assignedDate)
                : "-"}
            </div>
          </Col>
          <Col span={6}>
            <Text style={{ color: "#8A8FA3", fontWeight: 600, fontSize: 16 }}>
              Status
            </Text>
            <div
              style={{
                fontWeight: 600,
                fontSize: 16,
                color: "#232A36",
                marginTop: 2,
              }}
            >
              <Tag
                color={statusColors[KitDetail?.status?.label]}
                style={{
                  fontWeight: 600,
                  borderRadius: 8,
                  padding: "2px 12px",
                  fontSize: 16,
                  border: "none",
                  marginLeft: "-12px",
                }}
              >
                {KitDetail?.status?.label || ""}
              </Tag>
            </div>
          </Col>
        </Row>

        <Tabs
          defaultActiveKey="seals"
          style={{ display: "flex", justifyContent: "space-between" }}
          moreIcon={null}
        >
          <TabPane
            tab={
              <span
                style={{
                  fontWeight: 600,
                  fontSize: 16,
                  display: "inline-block",
                  textAlign: "center",
                }}
              >
                Seals ({sealData?.length || 0})
              </span>
            }
            key="seals"
          >
            <AssetTable
              dataSource={sealData}
              columns={sealColumns}
              rowKey="sealId"
            />
          </TabPane>
          <TabPane
            tab={
              <span
                style={{
                  fontWeight: 600,
                  fontSize: 16,
                  display: "inline-block",
                  textAlign: "center",
                }}
              >
                Boxes ({boxData?.length || 0})
              </span>
            }
            key="boxes"
          >
            <AssetTable
              dataSource={boxData}
              columns={boxColumns}
              rowKey="boxId"
            />
          </TabPane>
          <TabPane
            tab={
              <span
                style={{
                  fontWeight: 600,
                  fontSize: 16,
                  display: "inline-block",
                  textAlign: "center",
                }}
              >
                Stickers ({stickerData?.length || 0})
              </span>
            }
            key="stickers"
          >
            <AssetTable
              dataSource={stickerData}
              columns={stickerColumns}
              rowKey="stickerId"
            />
          </TabPane>
          <TabPane
            tab={
              <span
                style={{
                  fontWeight: 600,
                  fontSize: 16,
                  display: "inline-block",
                  textAlign: "center",
                }}
              >
                Devices ({deviceData?.length || 0})
              </span>
            }
            key="devices"
          >
            <AssetTable
              dataSource={deviceData}
              columns={deviceColumns}
              rowKey="uniqueId"
            />
          </TabPane>
        </Tabs>
      </div>

      <EditProjectCode
        isOpen={isEditProjectModalOpen}
        onClose={() => setIsEditProjectModalOpen(false)}
        KitDetail={KitDetail}
        fetchKitDetail={fetchKitDetail}
      />
      <ModalAfterAssetCreation
        kitData={kitDataAfterAssetCreation}
        isOpen={isOpenModalAfterAssetCreation}
        isClose={() => {
          setIsOpenModalAfterAssetCreation(false);
          setKitDataAfterAssetCreation({});
        }}
      />
      <ManageBoxOrders
        visible={isManageOrdersModalOpen}
        onClose={() => {
          setIsManageOrdersModalOpen(false);
          setSelectedBox(null);
        }}
        boxData={selectedBox}
        setBoxData={setSelectedBox}
        fetchKitDetail={fetchKitDetail}
      />
      <ChangeBoxStatus
        visible={isChangeStatusModalOpen}
        onClose={() => {
          setIsChangeStatusModalOpen(false);
          setSelectedBox(null);
          setTargetStatus(null);
        }}
        boxData={selectedBox}
        targetStatus={targetStatus}
        fetchKitDetail={fetchKitDetail}
      />
    </div>
  );
}
